import { useCallback, useEffect, useRef, useState } from "react";

const HISTORY_KEY = "mozzoappQrCatalog";
const ROOT = Object.freeze({ categoryId: null, subcategoryId: null });
const historyState = () => window.history.state && typeof window.history.state === "object" ? window.history.state : {};
const positionOf = (value) => ({
  categoryId: typeof value?.categoryId === "string" ? value.categoryId : null,
  subcategoryId: typeof value?.categoryId === "string" && typeof value?.subcategoryId === "string" ? value.subcategoryId : null,
});
const samePosition = (a, b) => a.categoryId === b.categoryId && a.subcategoryId === b.subcategoryId;
const depth = (position) => position.subcategoryId ? 2 : position.categoryId ? 1 : 0;

export default function useQrCatalogNavigation(scope = "") {
  const [position, setPosition] = useState(ROOT);
  const positionRef = useRef(ROOT);
  const rootRef = useRef("");
  const pendingBackRef = useRef(false);

  const commit = useCallback((next) => {
    positionRef.current = next;
    setPosition((previous) => samePosition(previous, next) ? previous : next);
  }, []);

  useEffect(() => {
    pendingBackRef.current = false;
    if (!scope) {
      commit(ROOT);
      return undefined;
    }
    const stored = historyState()[HISTORY_KEY];
    const ownsEntry = stored?.scope === scope && typeof stored?.root === "string";
    const initial = ownsEntry ? positionOf(stored) : ROOT;
    rootRef.current = ownsEntry ? stored.root : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    // Annotate, rather than push, the landing page: Back at the root must leave normally.
    window.history.replaceState({ ...historyState(), [HISTORY_KEY]: { scope, root: rootRef.current, ...initial } }, "", window.location.href);
    commit(initial);
    const pathname = window.location.pathname;
    const onPopState = (event) => {
      pendingBackRef.current = false;
      if (window.location.pathname !== pathname) return;
      const entry = event.state?.[HISTORY_KEY];
      if (entry?.scope === scope && entry.root === rootRef.current) {
        // Product/chatbot overlays inherit the same path, so closing them does not go up a category.
        commit(positionOf(entry));
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [commit, scope]);

  const navigate = (categoryId, subcategoryId = null) => {
    const next = positionOf({ categoryId, subcategoryId });
    const previous = positionRef.current;
    if (pendingBackRef.current || samePosition(previous, next)) return;
    if (!scope) {
      commit(next);
      return;
    }
    const state = historyState();
    const current = state[HISTORY_KEY];
    const ownsEntry = current?.scope === scope && current.root === rootRef.current && samePosition(positionOf(current), previous);
    const returningToParent = depth(next) < depth(previous) && (!next.categoryId || next.categoryId === previous.categoryId);
    if (ownsEntry && returningToParent) {
      pendingBackRef.current = true;
      window.history.go(depth(next) - depth(previous));
      return;
    }
    const entry = { ...state, [HISTORY_KEY]: { scope, root: rootRef.current, ...next } };
    if (returningToParent) {
      // A replaced history entry (for example, a payment return) must never rewind an unrelated page.
      window.history.replaceState(entry, "", window.location.href);
    } else {
      if (!ownsEntry) {
        window.history.replaceState({ ...state, [HISTORY_KEY]: { scope, root: rootRef.current, ...previous } }, "", window.location.href);
      }
      window.history.pushState(entry, "", window.location.href);
    }
    commit(next);
  };

  return { ...position, navigate };
}
